import { Link } from "react-router-dom";
import { Upload, FileText, ArrowRight } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

/**
 * V1.1a — Smart "Fix Credit" CTA.
 *
 * - User has NO completed credit report → "Upload Credit Report" (primary purple).
 * - User has one → "Review Credit Improvements" (secondary outline) to the
 *   Improvement Center.
 *
 * Completion is inferred from `latest_credit_report_id` (set on the user only
 * AFTER extraction succeeds — see `modules/credit_reports/router.py`).
 *
 * Props:
 *  - `size`: "sm" | "md" (default "md")
 *  - `align`: "start" | "stretch" (default "start") — `stretch` makes the
 *    button fill its container, useful inside cards.
 *  - `testid`: data-testid override for parent context disambiguation.
 */
export default function CreditReportCTA({ size = "md", align = "start", testid = "credit-report-cta", className = "" }) {
  const { user } = useAuth();
  const hasReport = Boolean(user?.latest_credit_report_id);
  const route = "/credit-report";

  const padding = size === "sm" ? "px-3.5 py-1.5 text-xs" : "px-4 py-2.5 text-sm";
  const stretch = align === "stretch" ? "w-full justify-center" : "";

  if (hasReport) {
    return (
      <Link
        to={route}
        data-testid={`${testid}-review`}
        className={`inline-flex items-center gap-1.5 rounded-md font-medium transition-all ${padding} ${stretch} ${className}`}
        style={{
          background: "transparent",
          color: "var(--v2-text)",
          border: "1px solid var(--v2-border)",
        }}
      >
        <FileText className="w-3.5 h-3.5" />
        Review Credit Improvements
        <ArrowRight className="w-3 h-3" />
      </Link>
    );
  }

  return (
    <Link
      to={route}
      data-testid={`${testid}-upload`}
      className={`inline-flex items-center gap-1.5 rounded-md font-medium transition-all ${padding} ${stretch} ${className}`}
      style={{
        background: "var(--v2-accent)",
        color: "var(--v2-accent-ink)",
      }}
    >
      <Upload className="w-3.5 h-3.5" />
      Upload Credit Report
      <ArrowRight className="w-3 h-3" />
    </Link>
  );
}
